import React, { useMemo, useState } from 'react';

import dayjs from '~/timeInit';

import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Chip,
  CircularProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';

import { DepartureInventory } from '~/types/tours/inventory';

import CostPriceTrendChart from './CostPriceTrendChart';
import InventoryDateSlider from './InventoryDateSlider';
import { ShareLabel, ShareList, hasLinkedShare } from './OfferSplit';
import {
  ScalePricingItem,
  availableCurrencies,
  buildTimeline,
  sharesByOffer,
  summariseDepartureCost,
} from './departureInventory';
import { formatAmount } from './formatAmount';
import { inventoryAtDate } from './inventorySnapshot';

interface Props {
  inventory?: DepartureInventory;
  snapshotDate?: number;
  onDateChange: (date: number) => void;
  /** The sliding scale rows the departure is priced by. */
  items: Array<ScalePricingItem>;
  isLoading: boolean;
  error?: { message: string } | null;
  showCosts?: boolean;
  showCostChart?: boolean;
  /** The tour's own offer, so travellers an LTLE built on it sold can be told apart. */
  tourOfferId?: string;
}

/** "5" -> "th", so a next-traveller hint reads as English rather than a count. */
function ordinalSuffix(value: number): string {
  if (value % 100 >= 11 && value % 100 <= 13) {
    return 'th';
  }

  return { 1: 'st', 2: 'nd', 3: 'rd' }[value % 10] ?? 'th';
}

/** One headline figure. Three of these carry the whole summary. */
function Tile({
  label,
  value,
  caption,
  children,
}: {
  label: string;
  value: string;
  caption?: string;
  children?: React.ReactNode;
}) {
  return (
    <Paper variant="outlined" sx={{ p: 2, minWidth: 200, flex: '1 1 200px' }}>
      <Typography variant="caption" color="text.secondary" display="block">
        {label}
      </Typography>
      <Typography variant="h3" fontWeight={600} sx={{ lineHeight: 1.3 }}>
        {value}
      </Typography>
      {!!caption && (
        <Typography variant="caption" color="text.secondary">
          {caption}
        </Typography>
      )}
      {children}
    </Paper>
  );
}

/** A detail section, closed until someone goes looking for it. */
function Section({ title, summary, children }: { title: string; summary: string; children: React.ReactNode }) {
  return (
    <Accordion
      disableGutters
      elevation={0}
      // Unmounted while closed: these sections hold the two largest tables on
      // the screen, and a closed section should cost nothing to render.
      TransitionProps={{ unmountOnExit: true }}
      sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1 }}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Stack direction="row" spacing={1.5} alignItems="baseline" flexWrap="wrap" useFlexGap>
          <Typography variant="subtitle1" fontWeight={600}>
            {title}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {summary}
          </Typography>
        </Stack>
      </AccordionSummary>
      <AccordionDetails>{children}</AccordionDetails>
    </Accordion>
  );
}

/**
 * Where a departure has got to: what it costs now, how that cost has moved, and
 * - closed beneath those - the arithmetic and the ledger behind it.
 */
export default function DepartureInventoryPanel({
  inventory,
  items,
  isLoading,
  error,
  snapshotDate,
  onDateChange,
  showCosts = true,
  showCostChart = showCosts,
  tourOfferId,
}: Props) {
  const currencies = useMemo(() => availableCurrencies(items), [items]);
  const [chosenCurrency, setChosenCurrency] = useState<string | null>(null);

  // Falls back rather than holding an invalid choice: switching departure can
  // leave a currency selected that the new one is not quoted in.
  const currency = chosenCurrency && currencies.includes(chosenCurrency) ? chosenCurrency : (currencies[0] ?? null);
  const currencyLabel = currency ?? '';

  const snapshot = useMemo(() => inventoryAtDate(inventory, snapshotDate), [inventory, snapshotDate]);
  const summary = useMemo(() => summariseDepartureCost(snapshot, items, { currency }), [snapshot, items, currency]);
  const shares = useMemo(
    () => sharesByOffer(snapshot?.entries ?? [], tourOfferId, (entry) => entry.packageConfigurationId),
    [snapshot, tourOfferId],
  );
  const isSplit = hasLinkedShare(shares);
  // The chart runs over the whole ledger, so it splits all of it rather than the snapshot.
  const chartShares = useMemo(
    () => sharesByOffer(inventory?.entries ?? [], tourOfferId, (entry) => entry.packageConfigurationId),
    [inventory, tourOfferId],
  );
  const timeline = useMemo(() => buildTimeline(inventory, items, currency), [inventory, items, currency]);

  if (isLoading) {
    return (
      <Box p={3} display="flex" justifyContent="center">
        <CircularProgress size={28} />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">Could not load this departure&apos;s inventory: {error.message}</Alert>;
  }

  const unpriced = summary.configurations.filter((configuration) => configuration.isUnpriced);

  if (!summary.units && !timeline.length) {
    const priced = summary.configurations.length;

    return (
      <Alert severity="info">
        Nothing has been sold against this departure yet, so there is no inventory to track.
        {priced > 0 &&
          ` Its ${priced} package option${priced > 1 ? 's are' : ' is'} ready — travellers appear here
        once a booking is confirmed.`}
      </Alert>
    );
  }

  const sold = summary.configurations.filter((configuration) => configuration.units !== 0).length;

  return (
    <Stack spacing={2.5} sx={{ minWidth: 0 }}>
      {/* 1. The figures someone came for. */}
      <Stack spacing={1}>
        {currencies.length > 1 && (
          <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap>
            <Typography variant="caption" color="text.secondary">
              Currency
            </Typography>
            <ToggleButtonGroup
              exclusive
              size="small"
              value={currency}
              onChange={(_event, value) => value && setChosenCurrency(value)}
            >
              {currencies.map((code) => (
                <ToggleButton key={code} value={code} sx={{ px: 1.5, py: 0.25 }}>
                  {code}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
          </Stack>
        )}

        <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
          <Tile
            label={isSplit ? 'Travellers on this departure' : 'Travellers sold'}
            value={String(summary.units)}
            caption="Net of releases"
          >
            {isSplit && (
              <Box mt={1}>
                <ShareList shares={shares} />
              </Box>
            )}
          </Tile>
          {showCosts && (
            <Tile
              label="Departure cost"
              value={summary.totalCost === null ? '—' : formatAmount(summary.totalCost)}
              caption={summary.totalCost === null ? 'Cannot be calculated' : `${currencyLabel} owed to the supplier`}
            />
          )}
        </Stack>
      </Stack>

      {/* 2. Anything that makes the figures untrustworthy, before the detail. */}
      {showCosts && !!unpriced.length && summary.seasonUsed !== null && (
        <Alert severity="warning">
          {unpriced.map((configuration) => `${configuration.label} (${configuration.units})`).join(', ')}{' '}
          {unpriced.length > 1 ? 'have travellers' : 'has travellers'} sold against{' '}
          {unpriced.length > 1 ? 'them' : 'it'} but no cost price, so the departure cost is incomplete.
        </Alert>
      )}

      {showCosts && summary.isBeyondScale && (
        <Alert severity="warning">
          {summary.configurations
            .filter((configuration) => configuration.isBeyondScale)
            .map((configuration) => `${configuration.label} (${configuration.units})`)
            .join(', ')}{' '}
          carries more travellers than the supplier quoted a price for, so it is costed at their largest quoted size —
          which the contract does not cover.
        </Alert>
      )}

      {showCosts && summary.seasonUsed === null && (
        <Alert severity="warning">
          {summary.departureSeason
            ? `This departure runs in ${summary.departureSeason}, which this supplier has not priced.`
            : 'No season is recorded against this departure.'}{' '}
          The supplier prices {summary.seasonsPriced.join(' and ')} at different rates, so there is no way to tell which
          applies — no cost is shown rather than showing the wrong one.
        </Alert>
      )}

      {/* 3. The trend - the reason this screen exists. */}
      {showCostChart && (
        <Stack spacing={1}>
          <Typography variant="subtitle1" fontWeight={600}>
            {showCosts ? 'Cost owed over time' : 'Inventory over time'}
          </Typography>
          <CostPriceTrendChart
            timeline={timeline}
            currency={currencyLabel}
            snapshotDate={snapshotDate}
            items={items}
            season={inventory?.season}
            showCosts={showCosts}
            shares={chartShares}
            tourOfferId={tourOfferId}
          />
          <InventoryDateSlider timeline={timeline} date={snapshotDate} onDateChange={onDateChange} />
        </Stack>
      )}

      {!showCostChart && <InventoryDateSlider timeline={timeline} date={snapshotDate} onDateChange={onDateChange} />}

      {/* 4. The arithmetic, and the ledger it came from - both closed. */}
      <Stack spacing={1}>
        {showCosts && (
          <Section
            title="How this cost is calculated"
            summary={`${sold} of ${summary.configurations.length} package options sold`}
          >
            <Stack spacing={2}>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Package option</TableCell>
                      <TableCell align="right">Travellers</TableCell>
                      <TableCell align="right">Price each</TableCell>
                      <TableCell align="right">Single supplement</TableCell>
                      <TableCell align="right">Cost</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {summary.configurations.map((configuration) => (
                      <React.Fragment key={configuration.packageConfigurationId}>
                        <TableRow hover>
                          <TableCell>
                            <Typography variant="body2" fontWeight="medium">
                              {configuration.label}
                            </Typography>
                            {!!configuration.season && (
                              <Typography variant="caption" color="text.secondary">
                                {configuration.season}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell align="right">
                            {configuration.units}
                            {!!configuration.upgradeTravellers.units && (
                              <Typography variant="caption" color="text.secondary" display="block">
                                +{configuration.upgradeTravellers.units} VIP
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell align="right">{formatAmount(configuration.unitPrice)}</TableCell>
                          {/* Count and amount in one column: two columns of dashes
                          said less than one line reading "1 x 11,660,000". */}
                          <TableCell align="right">
                            {configuration.singles + configuration.upgradeTravellers.singles
                              ? `${configuration.singles + configuration.upgradeTravellers.singles} × ${formatAmount(configuration.singleSupplement)}`
                              : '—'}
                          </TableCell>
                          <TableCell align="right">
                            {configuration.isUnpriced ? (
                              <Chip size="small" color="warning" label="No price" />
                            ) : (
                              <>
                                {formatAmount(configuration.cost)}
                                {!!configuration.nextTraveller && (
                                  <Typography variant="caption" color="text.secondary" display="block">
                                    {configuration.nextTraveller.addedCost < 0 ? '−' : '+'}
                                    {formatAmount(Math.abs(configuration.nextTraveller.addedCost))} for the{' '}
                                    {configuration.nextTraveller.pax}
                                    {ordinalSuffix(configuration.nextTraveller.pax)}
                                  </Typography>
                                )}
                              </>
                            )}
                          </TableCell>
                        </TableRow>
                        {isSplit &&
                          shares
                            .filter((share) => !!share.byConfiguration[configuration.packageConfigurationId])
                            .map((share) => (
                              <TableRow key={share.tourOfferId ?? 'own'}>
                                <TableCell sx={{ pl: 3, py: 0.5 }}>
                                  <ShareLabel share={share} />
                                </TableCell>
                                <TableCell align="right" sx={{ py: 0.5 }}>
                                  <Typography variant="caption" color="text.secondary">
                                    {share.byConfiguration[configuration.packageConfigurationId]}
                                  </Typography>
                                </TableCell>
                                <TableCell colSpan={3} sx={{ py: 0.5 }} />
                              </TableRow>
                            ))}
                      </React.Fragment>
                    ))}
                    <TableRow>
                      <TableCell colSpan={3} align="right">
                        <Typography variant="body2" fontWeight={600}>
                          Total {currencyLabel}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" fontWeight={600}>
                          {summary.totalCost === null ? '—' : formatAmount(summary.totalCost)}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </Stack>
          </Section>
        )}

        <Section title="Booking history" summary={`${timeline.length} change${timeline.length === 1 ? '' : 's'}`}>
          <Stack spacing={1.5}>
            <TableContainer sx={{ maxHeight: 420 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>When</TableCell>
                    <TableCell>Change</TableCell>
                    <TableCell>Room type</TableCell>
                    <TableCell>Booking</TableCell>
                    <TableCell>Package option</TableCell>
                    <TableCell align="right">Travellers</TableCell>
                    {showCosts && <TableCell align="right">Price each</TableCell>}
                    {showCosts && <TableCell align="right">Cost</TableCell>}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {timeline.map((point) => (
                    <TableRow key={point.entry.id} hover>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        {dayjs(point.occurredAt).format('D MMM YYYY')}
                      </TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          color={point.entry.operation === 'released' ? 'default' : 'success'}
                          // The stored operation's own words. "Added"/"Removed"
                          // read more plainly but describe the traveller, where
                          // these describe the inventory - which is the column.
                          label={point.entry.operation === 'released' ? 'Released' : 'Allocated'}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{point.entry.passenger.roomType ?? '—'}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{point.entry.reservation.bookingReference ?? '—'}</Typography>
                      </TableCell>
                      <TableCell>
                        {point.entry.tourConfiguration}-{point.entry.packageConfiguration}
                      </TableCell>
                      <TableCell align="right">{point.unitsAfter}</TableCell>
                      {/* This traveller's own option, at the group size after
                          their change - not a departure-wide rate, which would
                          show a VIP traveller the standard price. */}
                      {showCosts && <TableCell align="right">{formatAmount(point.entryUnitPrice)}</TableCell>}
                      {showCosts && (
                        <TableCell align="right">
                          {formatAmount(point.totalCost)}
                          {point.totalCostDelta !== null && point.totalCostDelta !== 0 && (
                            <Typography
                              variant="caption"
                              display="block"
                              sx={{ color: point.totalCostDelta < 0 ? 'success.main' : 'text.secondary' }}
                            >
                              {point.totalCostDelta < 0 ? '↓' : '↑'} {formatAmount(Math.abs(point.totalCostDelta))}
                            </Typography>
                          )}
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Stack>
        </Section>
      </Stack>
    </Stack>
  );
}
